import type {
  AgentCapabilities,
  AgentConfiguration,
  AgentEditorData,
  AgentKnowledgeBases,
  AgentValidation,
  AgentVersion,
  KnowledgeBase,
  KnowledgeSearchResult,
} from '@models/agent-configuration.model';
import backendHttpClient from '@services/_httpClient/backend.http-client';

import type { AgentConfigDto } from './dtos/agent.dto';
import {
  AgentsRepository,
  type ActivateVersionParams,
  type ProvisionSheetOutputParams,
  type ProvisionSheetOutputResult,
  type PublishAgentParams,
  type ReplaceConfigurationParams,
  type ReplaceKnowledgeBasesParams,
  type TestKnowledgeSearchParams,
} from './repository';
import type { AgentCallActivity, AgentResponse } from './types/agent.type';

class AgentsService extends AgentsRepository {
  public async activateVersion({
    id,
    versionId,
  }: ActivateVersionParams): Promise<void> {
    await backendHttpClient.post({
      path: `/agents/${id}/versions/${versionId}/activate`,
    });
  }

  public async create(payload: AgentConfigDto): Promise<AgentResponse> {
    const response = await backendHttpClient.post<
      AgentResponse,
      AgentConfigDto
    >({
      body: payload,
      path: '/agents',
    });

    return response.data;
  }

  public async getById(id: string): Promise<AgentResponse> {
    const response = await backendHttpClient.get<AgentResponse>({
      path: `/agents/${id}`,
    });

    return response.data;
  }

  public async getCapabilities(): Promise<AgentCapabilities> {
    const response = await backendHttpClient.get<AgentCapabilities>({
      path: '/agent-capabilities',
    });
    return response.data;
  }

  public async getConfiguration(id: string): Promise<AgentConfiguration> {
    const response = await backendHttpClient.get<AgentConfiguration>({
      path: `/agents/${id}/configuration`,
    });
    return response.data;
  }

  public async getEditorData(id: string): Promise<AgentEditorData> {
    const response = await backendHttpClient.get<AgentEditorData>({
      path: `/agents/${id}/editor-data`,
    });
    return response.data;
  }

  public async getKnowledgeBases(): Promise<Array<KnowledgeBase>> {
    const response = await backendHttpClient.get<Array<KnowledgeBase>>({
      path: '/knowledge-bases',
    });
    return response.data;
  }

  public async getKnowledgeSelection(id: string): Promise<AgentKnowledgeBases> {
    const response = await backendHttpClient.get<AgentKnowledgeBases>({
      path: `/agents/${id}/knowledge-bases`,
    });
    return response.data;
  }

  public async list(): Promise<Array<AgentResponse>> {
    const response = await backendHttpClient.get<Array<AgentResponse>>({
      path: '/agents',
    });

    return response.data;
  }

  public async listCalls(id: string): Promise<Array<AgentCallActivity>> {
    const response = await backendHttpClient.get<Array<AgentCallActivity>>({
      path: `/agents/${id}/calls`,
    });

    return response.data;
  }

  public async listVersions(id: string): Promise<Array<AgentVersion>> {
    const response = await backendHttpClient.get<Array<AgentVersion>>({
      path: `/agents/${id}/versions`,
    });
    return response.data;
  }

  public async provisionSheetOutput({
    expectedRevision,
    id,
    provisionRequestId,
  }: ProvisionSheetOutputParams): Promise<ProvisionSheetOutputResult> {
    const response = await backendHttpClient.post<
      ProvisionSheetOutputResult,
      { expectedRevision: number; provisionRequestId: string }
    >({
      body: { expectedRevision, provisionRequestId },
      path: `/agents/${id}/sheet-output/provision`,
    });
    return response.data;
  }

  public async publish({
    expectedRevision,
    id,
    publishRequestId,
  }: PublishAgentParams): Promise<AgentVersion> {
    const response = await backendHttpClient.post<
      AgentVersion,
      { expectedRevision: number; publishRequestId: string }
    >({
      body: { expectedRevision, publishRequestId },
      path: `/agents/${id}/publish`,
    });
    return response.data;
  }

  public async remove(id: string): Promise<void> {
    await backendHttpClient.delete<void>({ path: `/agents/${id}` });
  }

  public async replaceConfiguration({
    configuration,
    expectedRevision,
    id,
  }: ReplaceConfigurationParams): Promise<AgentConfiguration> {
    const response = await backendHttpClient.put<AgentConfiguration>({
      body: { configuration, expectedRevision },
      path: `/agents/${id}/configuration`,
    });
    return response.data;
  }

  public async replaceKnowledgeBases({
    expectedRevision,
    id,
    knowledgeBases,
  }: ReplaceKnowledgeBasesParams): Promise<AgentKnowledgeBases> {
    const response = await backendHttpClient.put<AgentKnowledgeBases>({
      body: { expectedRevision, knowledgeBases },
      path: `/agents/${id}/knowledge-bases`,
    });
    return response.data;
  }

  public async testKnowledgeSearch({
    id,
    question,
  }: TestKnowledgeSearchParams): Promise<KnowledgeSearchResult> {
    const response = await backendHttpClient.post<
      KnowledgeSearchResult,
      { question: string }
    >({
      body: { question },
      path: `/agents/${id}/knowledge-bases/test-search`,
    });
    return response.data;
  }

  public async validate(id: string): Promise<AgentValidation> {
    const response = await backendHttpClient.post<AgentValidation>({
      path: `/agents/${id}/validate`,
    });
    return response.data;
  }
}

export const agentsService = new AgentsService();
